/**
 * «Пользователи» (только администратор): приглашения, лимит Claude, ссылка сброса пароля, блокировка,
 * роль, удаление.
 */
import type { AdminActivity, AdminUser } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, del, get, patch, post } from '../api/client';
import { useMe } from '../api/hooks';
import { ConfirmButton, useUI } from '../components/overlays';
import { Avatar, Icon, PageHead } from '../components/ui';
import { Box } from './Settings';

interface Invite {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
}
interface CreatedLink {
  url: string;
  expiresAt: string;
}

const usersKey = ['admin', 'users'] as const;
const activityKey = ['admin', 'activity'] as const;
const invitesKey = ['admin', 'invites'] as const;

const dt = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
const n = (v: number) => v.toLocaleString('ru-RU');
const mb = (b: number) => (b < 1048576 ? `${Math.ceil(b / 1024)} КБ` : `${(b / 1048576).toFixed(1)} МБ`);

/** Ссылка для передачи человеку: поле только для чтения и «Скопировать». */
function LinkBox({ link, note }: { link: CreatedLink; note: string }) {
  const ui = useUI();
  return (
    <div className="pform" role="status">
      <b>{note}</b>
      <div className="linkbox">
        <input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} aria-label="Ссылка" />
        <button
          className="btn sm"
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link.url);
              ui.toast('Ссылка скопирована');
            } catch {
              ui.toast('Выделите ссылку и скопируйте вручную');
            }
          }}
        >
          Скопировать
        </button>
      </div>
      <p className="muted sm" style={{ margin: '6px 0 0' }}>
        Действует до {dt(link.expiresAt)}, один раз. Отправьте её сами — в мессенджере или письмом.
      </p>
    </div>
  );
}

function Invites() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: invitesKey, queryFn: () => get<Invite[]>('/admin/invites') });
  const [link, setLink] = useState<CreatedLink | null>(null);
  const [err, setErr] = useState('');
  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = String(new FormData(form).get('email') ?? '').trim();
    setErr('');
    try {
      setLink(await post<CreatedLink>('/admin/invites', { email }));
      form.reset();
      await qc.invalidateQueries({ queryKey: invitesKey });
    } catch (x) {
      setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось');
    }
  };
  return (
    <Box title="Пригласить">
      <p className="muted sm" style={{ marginTop: -6 }}>
        Ссылка на 7 дней: по ней человек сам задаёт пароль. Почта — необязательно; если указать,
        зарегистрироваться можно только с ней.
      </p>
      <form className="toolbar" style={{ margin: 0 }} onSubmit={create}>
        <label className="search" style={{ minHeight: 42 }}>
          <Icon name="mail" size={18} />
          <input
            type="email"
            name="email"
            placeholder="friend@example.com (необязательно)"
            aria-label="Почта"
          />
        </label>
        <button className="btn primary" type="submit">
          <Icon name="plus" /> Создать приглашение
        </button>
      </form>
      {err && (
        <p className="sm" role="alert" style={{ color: 'var(--danger)' }}>
          {err}
        </p>
      )}
      {link && <LinkBox link={link} note="Приглашение готово" />}
      {(list.data ?? []).length > 0 && (
        <>
          <h3 style={{ margin: '14px 0 6px', fontSize: 14 }}>Ждут регистрации</h3>
          <div className="list">
            {list.data!.map((i) => (
              <div className="lrow" key={i.id}>
                <div className="lm">
                  <b>{i.email || 'любая почта'}</b>
                  <div className="meta">
                    создано {dt(i.createdAt)} · до {dt(i.expiresAt)}
                  </div>
                </div>
                <ConfirmButton
                  className="btn sm ghost danger"
                  confirmText="Отозвать?"
                  onConfirm={async () => {
                    await del(`/admin/invites/${i.id}`);
                    await qc.invalidateQueries({ queryKey: invitesKey });
                  }}
                >
                  Отозвать
                </ConfirmButton>
              </div>
            ))}
          </div>
        </>
      )}
    </Box>
  );
}

function LimitField({ u, onSave }: { u: AdminUser; onSave: (v: number | null) => Promise<void> }) {
  const [v, setV] = useState(u.aiMonthlyTokenLimit == null ? '' : String(u.aiMonthlyTokenLimit));
  const pct = u.aiMonthlyTokenLimit ? Math.round((u.tokensThisMonth / u.aiMonthlyTokenLimit) * 100) : null;
  return (
    <div>
      <div className="sm">
        {n(u.tokensThisMonth)}
        {pct != null && <span className={pct >= 80 ? 'late' : 'muted'}> · {pct} %</span>}
      </div>
      <input
        style={{ marginTop: 4, maxWidth: 140 }}
        inputMode="numeric"
        aria-label={`Лимит токенов ${u.email}`}
        placeholder="без лимита"
        value={v}
        onChange={(e) => setV(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => {
          const next = v ? Number(v) : null;
          if (next !== u.aiMonthlyTokenLimit) void onSave(next);
        }}
      />
    </div>
  );
}

function UserRow({ u, me }: { u: AdminUser; me: string }) {
  const ui = useUI();
  const qc = useQueryClient();
  const [link, setLink] = useState<CreatedLink | null>(null);
  const self = u.id === me;
  const change = async (body: Record<string, unknown>, ok: string) => {
    try {
      qc.setQueryData(usersKey, await patch<AdminUser[]>(`/admin/users/${u.id}`, body));
      ui.toast(ok);
    } catch (x) {
      ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
    }
  };
  return (
    <>
      <tr>
        <td>
          <div className="tc">
            <Avatar name={u.name || u.email} size={30} />
            <div>
              <b>{u.name || u.email}</b>
              <span>
                {u.name ? u.email : ''} {u.role === 'admin' && <span className="tag adm">админ</span>}{' '}
                {u.blocked && <span className="tag blk">заблокирован</span>}
              </span>
            </div>
          </div>
        </td>
        <td className="sm">{dt(u.lastSeenAt)}</td>
        <td className="num">{u.activeDays30}</td>
        <td className="num">{u.applications}</td>
        <td>
          <LimitField u={u} onSave={(v) => change({ aiMonthlyTokenLimit: v }, 'Лимит сохранён')} />
        </td>
        <td className="sm">
          {u.mail ? (u.mail === 'imap' ? 'почта: пароль прил.' : 'почта: Google') : 'почта —'}
          <br />
          {u.telegramLinked ? 'Telegram ✓' : 'Telegram —'}
          <br />
          файлы {mb(u.filesBytes)}
        </td>
        <td>
          <div className="actions">
            <button
              className="btn sm"
              onClick={async () => {
                try {
                  setLink(await post<CreatedLink>(`/admin/users/${u.id}/reset-link`));
                } catch (x) {
                  ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
                }
              }}
            >
              Ссылка сброса пароля
            </button>
            {!self && (
              <>
                <button
                  className="btn sm ghost"
                  onClick={() =>
                    change(
                      { role: u.role === 'admin' ? 'user' : 'admin' },
                      u.role === 'admin' ? 'Теперь обычный пользователь' : 'Теперь администратор',
                    )
                  }
                >
                  {u.role === 'admin' ? 'Снять админа' : 'Сделать админом'}
                </button>
                <button
                  className="btn sm ghost"
                  onClick={() =>
                    change(
                      { blocked: !u.blocked },
                      u.blocked ? 'Доступ возвращён' : 'Заблокирован, сессии закрыты',
                    )
                  }
                >
                  {u.blocked ? 'Разблокировать' : 'Заблокировать'}
                </button>
                <ConfirmButton
                  className="btn sm ghost danger"
                  confirmText="Удалить со всеми данными?"
                  onConfirm={async () => {
                    try {
                      await del(`/admin/users/${u.id}`);
                      await qc.invalidateQueries({ queryKey: usersKey });
                      ui.toast('Пользователь удалён');
                    } catch (x) {
                      ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
                    }
                  }}
                >
                  Удалить
                </ConfirmButton>
              </>
            )}
          </div>
        </td>
      </tr>
      {link && (
        <tr>
          <td colSpan={6}>
            <LinkBox link={link} note={`Ссылка для нового пароля: ${u.email}`} />
          </td>
        </tr>
      )}
    </>
  );
}

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const dayLabel = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

/** DAU / WAU / MAU и столбики «сколько человек заходило» за 30 дней. */
function Activity() {
  const q = useQuery({ queryKey: activityKey, queryFn: () => get<AdminActivity>('/admin/activity') });
  const a = q.data;
  if (!a) return null;
  const top = Math.max(1, ...a.days.map((d) => d.users));
  const tiles: [string, string, string][] = [
    ['DAU', String(a.dau), 'заходили сегодня'],
    ['WAU', String(a.wau), 'за 7 дней'],
    ['MAU', String(a.mau), `за 30 дней из ${a.users}`],
    ['DAU / MAU', `${pct(a.dau, a.mau)} %`, 'доля ежедневных'],
  ];
  return (
    <Box title="Активность">
      <p className="muted sm" style={{ marginTop: -6 }}>
        Уникальные пользователи, открывавшие трекер (дни — по UTC). Считается с 31 октября 2026; более ранние
        дни — только по сохранённым входам.
      </p>
      <div className="act-tiles">
        {tiles.map(([k, v, s]) => (
          <div className="act-tile" key={k}>
            <span>{k}</span>
            <b>{v}</b>
            <small>{s}</small>
          </div>
        ))}
      </div>
      <div className="act-chart" role="img" aria-label="Активные пользователи по дням за 30 дней">
        {a.days.map((d) => (
          <div className="act-col" key={d.day} title={`${dayLabel(d.day)}: ${d.users}`}>
            <span
              className={`act-bar${d.users ? '' : ' zero'}`}
              style={{ height: `${(d.users / top) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="act-axis muted sm" aria-hidden="true">
        <span>{dayLabel(a.days[0]!.day)}</span>
        <span>сегодня</span>
      </div>
    </Box>
  );
}

export function AdminPage() {
  const me = useMe().data?.user;
  const users = useQuery({ queryKey: usersKey, queryFn: () => get<AdminUser[]>('/admin/users') });
  if (me?.role !== 'admin')
    return <PageHead title="Пользователи" sub="Раздел доступен только администратору" />;
  return (
    <>
      <PageHead title="Пользователи" sub="Кто пользуется трекером, приглашения и лимиты Claude" />
      <Activity />
      <Invites />
      <Box title={`Пользователи · ${users.data?.length ?? '…'}`}>
        <p className="muted sm" style={{ marginTop: -6 }}>
          Лимит — токенов Claude в месяц на человека (пусто — без лимита); ключ Anthropic общий. Данные
          каждого видит только он сам.
        </p>
        <div className="tblw" tabIndex={0} role="region" aria-label="Пользователи">
          <table className="tbl adm-tbl">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Был в сети</th>
                <th className="num">Дней из 30</th>
                <th className="num">Вакансий</th>
                <th>Claude за месяц / лимит</th>
                <th>Подключения</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((u) => (
                <UserRow key={u.id} u={u} me={me.id} />
              ))}
            </tbody>
          </table>
        </div>
      </Box>
    </>
  );
}
